/**
 * First-party product analytics: a minimal funnel tracker.
 *
 * Emits anonymous funnel events to `POST /analytics/events`. Deliberately tiny:
 * no third-party SDK, no advertising pixel, no cross-site tracker. The privacy
 * policy (`features/legal/privacy-policy-content.ts`, sections 2.5, 3, 7 and 8)
 * is written to be true of exactly this module; keep the two in lockstep.
 */

import { type AnalyticsEventRequest, trackAnalyticsEvent } from "@/lib/api";
import { logger } from "@/lib/logger";

// The allow-listed funnel event names. The backend inlines this union on the
// request body rather than emitting a standalone schema, so we source it from
// the generated type instead of hand-writing it: add/remove an event server-
// side and this union tracks it automatically.
type AnalyticsEventName = AnalyticsEventRequest["event_name"];

// A per-visit random id, held ONLY in this tab's memory (a module-level variable,
// never written to a cookie, localStorage, or sessionStorage): keeping it out of
// browser storage is what keeps the privacy policy's section 7 "no consent banner"
// reasoning airtight. Accepted tradeoff: the id rotates across the full-page
// sign-in redirect, so the gate→sign-in funnel is measured by global event counts,
// not by per-session joins.
//
// Initialized lazily on first use, never at module-evaluation time:
// `crypto.randomUUID` is secure-context-only and is undefined on a plain-HTTP,
// non-localhost origin (e.g. `vite --host` opened from a phone). Touching it at
// import time would throw and blank every page that statically imports analytics,
// which would violate this module's "analytics can never break the app" contract.
let sessionId: string | null = null;

function getSessionId(): string {
  if (sessionId === null) {
    // This id is a correlator, not a credential, so where crypto.randomUUID is
    // unavailable (insecure context / old browser) a non-crypto v4-shaped id is
    // acceptable: the backend only requires a UUID-formatted string.
    sessionId =
      typeof crypto !== "undefined" && typeof crypto.randomUUID === "function"
        ? crypto.randomUUID()
        : "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (char) => {
            const rand = (Math.random() * 16) | 0;
            const value = char === "x" ? rand : (rand & 0x3) | 0x8;
            return value.toString(16);
          });
  }
  return sessionId;
}

// trackOnce dedupe guard: client-side only, never sent to the server.
const firedKeys = new Set<string>();

/**
 * Fire-and-forget a funnel event. Never throws and never blocks the UI: the
 * configured SDK client rejects on a non-2xx response, so every failure is
 * swallowed: analytics must not be able to break the app.
 */
export function track(eventName: AnalyticsEventName, payload?: Record<string, string>): void {
  logger.debug("[analytics]", eventName, payload);
  void trackAnalyticsEvent({
    body: { session_id: getSessionId(), event_name: eventName, payload: payload ?? {} },
  }).catch(() => {
    // Intentionally ignored: a dropped analytics event is never worth surfacing.
  });
}

/**
 * Like `track`, but fires at most once per `key` for the lifetime of the tab.
 * Guards mount-time events against StrictMode's double-invocation and against
 * remounts. The `key` stays client-side only: never send it to the server.
 */
export function trackOnce(
  key: string,
  eventName: AnalyticsEventName,
  payload?: Record<string, string>,
): void {
  if (firedKeys.has(key)) {
    return;
  }
  firedKeys.add(key);
  track(eventName, payload);
}
